import React, { createContext, memo, useEffect, useMemo, useReducer, useRef } from 'react'
import debug from 'debug'
import context from '@/context'
import { HubConnectionBuilder } from '@microsoft/signalr'
import moment from 'moment'
import { useSelector } from 'react-redux'

const log = debug('app:SignalrContext')

export const SignalrContext = createContext(null)

const initialState = {
  status: 'off',
  connection: null
}

function reducer (state = initialState, action) {
  log('reducer action: %s, %o', action.type, action)
  if (state.status === 'off' && action.type !== 'tokenChanged') return state
  switch (action.type) {
    case 'connect':
      return { ...state, status: 'connecting', connection: action.connection }
    case 'connected':
    case 'error':
    case 'off':
      return state.status !== 'off' ? { ...state, status: action.type } : state
    case 'connecting':
      return { ...state, status: action.type }
    case 'retry':
      return state.status === 'error' ? { ...state, status: 'connecting' } : state
    case 'tokenChanged':
      if (!action.token) return { ...state, status: 'off', connection: null }
      return state.status !== 'connected' ? { ...state, status: 'connecting' } : state
  }
  return state
}

export const SignalrProvider = memo(function SignalrProvider ({ children }) {
  const encjwt = useSelector(state => (state.identity.signedIn && state.identity.user && state.identity.user.profile && state.identity.user.profile.encjwt) || '')
  const exp = useSelector(state => (state.identity.user && state.identity.user.profile && state.identity.user.profile.exp) || 0)
  const now = moment().unix()
  const tokenExpired = now > exp
  const token = useMemo(() => encjwt && !tokenExpired ? encjwt : '', [encjwt, tokenExpired])
  const [{ connection, status }, dispatch] = useReducer(reducer, initialState)
  // no need to reconnect on each token change, so we use ref and when new connection is needed, we use current token
  const tokenRef = useRef('')
  useEffect(() => {
    tokenRef.current = token
    dispatch({ type: 'tokenChanged', token })
  }, [token])

  const failure = status === 'error'

  // when error occurs, retry after delay
  useEffect(() => {
    let handle = 0
    if (failure) {
      handle = setTimeout(() => {
        dispatch({ type: 'retry' })
      }, 15000)
    }
    return () => {
      if (handle) {
        clearTimeout(handle)
      }
    }
  }, [failure])

  const isConnecting = status === 'connecting'
  // create new connection
  useEffect(() => {
    if (isConnecting) {
      let newConnection = null
      const token = tokenRef.current
      if (token) {
        const url = `${context.config.apiGatewayUrl}signalr/common?enc_auth_token=${encodeURIComponent(token)}`
        newConnection = new HubConnectionBuilder()
          .withUrl(url)
          .build()
        newConnection.onclose((err) => {
          log(`Hub connection closed`)
          dispatch({ type: 'error', err })
        })
        dispatch({ type: 'connect', connection: newConnection })
      }
    }
  }, [isConnecting])

  // when new HubConnection object is created, start it
  useEffect(() => {
    if (connection) {
      log('connecting to signalR hub...')
      connection.start().then(() => {
        log(`Connected to signalR hub.`, connection.connectionId)
        dispatch({ type: 'connected' })
      }).catch(err => {
        log('Connecting to signalR hub failed.', err)
        dispatch({ type: 'error' })
      })
    }
    return () => {
      if (connection) {
        log('signalR disconnecting...')
        try {
          connection.stop()
          log('signalR disconnected')
        } catch (err) {
          log('Failed to disconnect signalR', err)
        }
      }
    }
  }, [connection])
  return (
    <SignalrContext.Provider value={connection}>{children}</SignalrContext.Provider>
  )
})



// WEBPACK FOOTER //
// ./src/components/signalrProvider.js